import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en_hk from "./en_hk";
import zh_hk from "./zh_hk";

const SUPPORTED = ["en_hk", "zh_hk"] as const;

// i18next stores and mutates the objects passed to init, so keep an untouched copy
// for restoring a key after its override is removed.
const BUNDLED = {
  en_hk: structuredClone(en_hk),
  zh_hk: structuredClone(zh_hk),
};

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en_hk: { translation: en_hk },
      zh_hk: { translation: zh_hk },
    },
    supportedLngs: [...SUPPORTED],
    fallbackLng: "en_hk",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "wedding_lang",
      convertDetectedLanguage(lng: string) {
        const l = lng.toLowerCase().replace("-", "_");
        if (l.startsWith("zh")) return "zh_hk";
        return "en_hk";
      },
    },
  });

/**
 * Call after fetching settings to apply admin default language
 * when user hasn't explicitly chosen one.
 */
export function applyDefaultLanguage(defaultLang: string) {
  if (
    !localStorage.getItem("wedding_lang") &&
    SUPPORTED.includes(defaultLang as (typeof SUPPORTED)[number])
  ) {
    i18next.changeLanguage(defaultLang);
  }
}

/**
 * Read `locale_overrides_<lang>` values from a settings map.
 * Invalid JSON is ignored, matching the previous per-app parsers.
 */
export function readLocaleOverrides(
  settings: Record<string, string>,
): Record<string, Record<string, unknown>> {
  const overrides: Record<string, Record<string, unknown>> = {};
  for (const lang of SUPPORTED) {
    const raw = settings[`locale_overrides_${lang}`];
    if (!raw) continue;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        overrides[lang] = parsed as Record<string, unknown>;
      }
    } catch {
      /* ignore invalid JSON */
    }
  }
  return overrides;
}

/**
 * Merge locale overrides (from settings) into the running i18next instance.
 * Re-applies the bundled catalogs first so a removed override falls back to the default text.
 */
export function applyLocaleOverrides(
  overrides: Partial<Record<(typeof SUPPORTED)[number], object>>,
) {
  for (const lang of SUPPORTED) {
    i18next.addResourceBundle(lang, "translation", BUNDLED[lang], true, true);
    const override = overrides[lang];
    if (override && typeof override === "object") {
      i18next.addResourceBundle(lang, "translation", override, true, true);
    }
  }
  i18next.emit("languageChanged", i18next.language);
}
